import { createContext, useContext, useState, useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const AdminContext = createContext()

export const useAdmin = () => {
  const context = useContext(AdminContext)
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
}

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [siteMode, setSiteMode] = useState('portfolio') // portfolio or ecommerce
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing admin session and validate token
    const checkAuthStatus = async () => {
      const adminToken = localStorage.getItem('adminToken')
      const adminUser = localStorage.getItem('adminUser')
      
      if (adminToken && adminUser) {
        try {
          // Verify token with a simple API call
          const response = await fetch(`${API_BASE_URL}/api/categories`, {
            headers: {
              'Authorization': `Bearer ${adminToken}`
            }
          })
          
          if (response.ok || response.status === 401) {
            // Token is valid or we got a proper 401 response
            const userData = JSON.parse(adminUser)
            setUser(userData)
            setIsAuthenticated(true)
          } else {
            // Token is invalid, clear storage
            localStorage.removeItem('adminToken')
            localStorage.removeItem('adminUser')
          }
        } catch (error) {
          // Network error, assume token is still valid
          try {
            const userData = JSON.parse(adminUser)
            setUser(userData)
            setIsAuthenticated(true)
          } catch (parseError) {
            localStorage.removeItem('adminToken')
            localStorage.removeItem('adminUser')
          }
        }
      }
      
      // Fetch current site mode from backend
      await fetchSiteMode()
      setLoading(false)
    }
    
    checkAuthStatus()
  }, [])

  // Fetch site mode from backend
  const fetchSiteMode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/site-config`)
      if (response.ok) {
        const config = await response.json()
        setSiteMode(config.mode || 'portfolio')
        localStorage.setItem('siteMode', config.mode || 'portfolio')
      }
    } catch (error) {
      console.error('Error fetching site mode:', error)
      // Fallback to localStorage if API fails
      const savedMode = localStorage.getItem('siteMode') || 'portfolio'
      setSiteMode(savedMode)
    }
  }

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const userData = {
          id: 1,
          username: data.user.username,
          name: 'SSP Biotech Admin',
          email: 'admin@sspbiotech.in',
          role: data.user.role,
          loginTime: new Date().toISOString()
        }
        
        setUser(userData)
        setIsAuthenticated(true)
        
        // Store the actual JWT token from backend
        localStorage.setItem('adminToken', data.token)
        localStorage.setItem('adminUser', JSON.stringify(userData))
        
        return { success: true, user: userData }
      } else {
        return { success: false, message: data.message || 'Invalid credentials' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, message: 'Login failed' }
    }
  }

  const logout = () => {
    setIsAuthenticated(false)
    setUser(null)
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminUser')
  }

  const updateSiteMode = async (mode) => {
    try {
      // Call the backend API to update site mode
      const response = await fetch(`${API_BASE_URL}/api/admin/site-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode,
          adminUsername: user?.username || 'admin',
          adminPassword: 'ssp2024admin'
        })
      })

      const data = await response.json()

      if (response.ok) {
        setSiteMode(mode)
        localStorage.setItem('siteMode', mode)
        return { success: true, message: data.message }
      } else {
        return { success: false, message: data.message }
      }
    } catch (error) {
      console.error('Failed to update site mode:', error)
      return { success: false, message: 'Failed to update site mode' }
    }
  }

  const value = {
    isAuthenticated,
    user,
    siteMode,
    loading,
    login,
    logout,
    updateSiteMode
  }

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  )
}
